import { apiHelper } from "@/utils";
import { API } from "@/constants";

export const userService = {
  getAll,
  getPermissionDefinitions,
  store,
  show,
  update,
  updateStatus,
  unlock,
  approveAppAccess,
  updateAppAccess,
  softDelete,
  destroy,
};

const MODULE = {
  USER: "users",
};

const API_URL = API.BASE_URL_API + "/v1";

export function getPermissionDefinitions() {
  return apiHelper(API_URL).get("/user-permissions/definitions");
}

export function getAll(filter) {
  const request = {
    params: {
      company_uuid: filter?.companyUUID,
      user_type: filter?.userType,
      page: filter?.pageNumber,
      size: filter?.pageSize,
      is_active: filter?.isActive,
    },
  };

  let array = filter?.searchArray;
  array?.forEach((word, index) => {
    if (word != null || word != "") {
      var searchParam = `search_words[${index}]`;
      request.params[searchParam] = word;
    }
  });

  return apiHelper(API_URL).get(`/${MODULE.USER}`, request);
}

export function store(data) {
  const formData = new FormData();
  if (data.image instanceof File) formData.append(`image`, data.image);

  // user
  if (data.roleUUID) formData.append(`role_uuid`, data.roleUUID);
  if (data.firstName) formData.append(`first_name`, data.firstName);
  if (data.lastName) formData.append(`last_name`, data.lastName);
  if (data.email) formData.append(`email`, data.email);
  if (data.phoneCode) formData.append(`phone_code`, data.phoneCode);
  if (data.phone) formData.append(`phone`, data.phone);
  if (data.phoneIso) formData.append(`phone_iso`, data.phoneIso);
  if (data.iwowsoftEmployeeUUID) formData.append(`iwowsoft_employee_uuid`, data.iwowsoftEmployeeUUID);
  if (data.portalPassword) formData.append(`portal_password`, data.portalPassword);
  if (data.portalAccess != null) formData.append(`portal_access`, data.portalAccess);
  if (data.appPassword) formData.append(`app_password`, data.appPassword);
  if (data.appAccess != null) formData.append(`app_access`, data.appAccess);
  if (data.userType != null) formData.append(`user_type`, data.userType); // 0 - internal, 1 - external

  // permissions
  Object.entries(data.permission || {}).forEach(([key, level]) => {
    if (level != null) formData.append(`permission[${key}]`, level);
  });

  // user companies
  if (Array.isArray(data.userCompanies) && data.userCompanies.length > 0) {
    data.userCompanies.forEach((userCompany, index) => {
      if (userCompany.companyUUID) formData.append(`user_companies[${index}][company_uuid]`, userCompany.companyUUID);
      if (userCompany.departmentUUID) formData.append(`user_companies[${index}][department_uuid]`, userCompany.departmentUUID);
      if (userCompany.officeUUID) formData.append(`user_companies[${index}][office_uuid]`, userCompany.officeUUID);
      if (userCompany.positionUUID) formData.append(`user_companies[${index}][position_uuid]`, userCompany.positionUUID);
    });
  }

  return apiHelper(API_URL).post(`/${MODULE.USER}`, formData);
}

export function show(uuid) {
  return apiHelper(API_URL).get(`/${MODULE.USER}/${uuid}`);
}

export function update(data) {
  const formData = new FormData();
  formData.append(`_method`, "PUT");
  if (data.image instanceof File) formData.append(`image`, data.image);
  if (data.userType != null) formData.append(`user_type`, data.userType);

  // user
  if (data.roleUUID) formData.append(`role_uuid`, data.roleUUID);
  if (data.firstName) formData.append(`first_name`, data.firstName);
  if (data.lastName) formData.append(`last_name`, data.lastName);
  if (data.email) formData.append(`email`, data.email);
  if (data.phoneCode) formData.append(`phone_code`, data.phoneCode);
  if (data.phone) formData.append(`phone`, data.phone);
  if (data.phoneIso) formData.append(`phone_iso`, data.phoneIso);
  if (data.iwowsoftEmployeeUUID) formData.append(`iwowsoft_employee_uuid`, data.iwowsoftEmployeeUUID);
  if (data.portalPassword) formData.append(`portal_password`, data.portalPassword);
  if (data.portalAccess != null) formData.append(`portal_access`, data.portalAccess);
  if (data.appPassword) formData.append(`app_password`, data.appPassword);
  if (data.appAccess != null) formData.append(`app_access`, data.appAccess);

  // permissions
  Object.entries(data.permission || {}).forEach(([key, level]) => {
    if (level != null) formData.append(`permission[${key}]`, level);
  });

  // user companies
  if (Array.isArray(data.userCompanies) && data.userCompanies.length > 0) {
    data.userCompanies.forEach((userCompany, index) => {
      if (userCompany.companyUUID) formData.append(`user_companies[${index}][company_uuid]`, userCompany.companyUUID);
      if (userCompany.departmentUUID) formData.append(`user_companies[${index}][department_uuid]`, userCompany.departmentUUID);
      if (userCompany.officeUUID) formData.append(`user_companies[${index}][office_uuid]`, userCompany.officeUUID);
      if (userCompany.positionUUID) formData.append(`user_companies[${index}][position_uuid]`, userCompany.positionUUID);
    });
  }

  return apiHelper(API_URL).post(`/${MODULE.USER}/${data.uuid}`, formData);
}

export function updateStatus(data) {
  const request = {
    is_active: data.isActive,
  };

  return apiHelper(API_URL).patch(`/${MODULE.USER}/status/${data.uuid}`, request);
}

export function unlock(data) {
  const request = {
    is_app: data.isApp,
    is_portal: data.isPortal,
  };
  return apiHelper(API_URL).patch(`/${MODULE.USER}/unlock/${data.uuid}`, request);
}

export function approveAppAccess(uuid) {
  const appBaseURL = API.BASE_URL_APP || API.BASE_URL_API.replace(/\/api\/?$/, "/app-api");
  return apiHelper(`${appBaseURL.replace(/\/$/, "")}/v1`).patch(`/users/updateStatus/${encodeURIComponent(uuid)}`, { app_access: 1 });
}

export function updateAppAccess(uuid, appAccess) {
  return apiHelper(API_URL).patch(`/users/${encodeURIComponent(uuid)}/app-access`, {app_access: appAccess});
}

export function softDelete(data) {
  return apiHelper(API_URL).patch(`/${MODULE.USER}/delete/${data.uuid}`);
}

export function destroy(data) {
  return apiHelper(API_URL).delete(`/${MODULE.USER}/${data.uuid}`);
}
